import React, { useContext, useEffect, useRef, useState } from "react";

import { AppContext } from "../../../util/context";
import { DeviceCheckScreen, DeviceCheckStep, DeviceData } from "./StepDeviceCheck.types";
import { Field, StepData } from "../../FormViewer/FormViewer.types";
import { evaluateCompat } from "../../../util/deviceCompat";
import { DeviceInfo, getDeviceInfo } from "../../../util/deviceInfo";
import Screen1BManual from "./screens/Screen1BManual";

import block from "bem-css-modules";
import styles from "./StepDeviceCheck.module.scss";

// This step only DETECTS the device (auto, or manual selection when detection
// fails / the visitor is on desktop), writes the result to the session layer,
// and advances. It renders no "supported/unsupported" outcome screens: the
// verdict is pushed as `device_compat`, and ordinary CMS page steps gated with
// `hideIfFieldExists` present the appropriate message to the user afterwards.
const StepDeviceCheck = (props: { stepData: DeviceCheckStep; stepIndex: number }) => {
  const context = useContext(AppContext);
  const { handleStepSubmit, activeStepIndex } = context;
  const { stepData } = props;
  const {
    stepSettings: [{ uuid, label, nextStep, lastStep, classes, customNextButton }],
  } = stepData;

  const b = block(styles);

  const [initialInfo, setInitialInfo] = useState<DeviceInfo | null>(null);
  const [screen, setScreen] = useState<DeviceCheckScreen | null>(null);
  const [detectionFailed, setDetectionFailed] = useState(false);

  const hasAutoAdvanced = useRef(false);
  useEffect(() => {
    getDeviceInfo().then((info) => {
      setInitialInfo(info);
      const isMobile = info.is_mobile === "true";

      // Desktop or undetected OS: ask the visitor to select the device they'll
      // actually use for therapy.
      if (!isMobile || !info.device_os) {
        setDetectionFailed(!info.device_os);
        setScreen(info.device_os ? "SCREEN_1B_MANUAL" : "SCREEN_1B_FALLBACK");
        return;
      }

      // Mobile with a detected OS: submit the verdict silently and move on.
      if (activeStepIndex !== props.stepIndex) return;
      if (hasAutoAdvanced.current) return;
      hasAutoAdvanced.current = true;

      submitOutcome({
        source: "auto",
        os: info.device_os,
        osVersion: info.device_os_version,
        family: info.device_family,
        isMobile: true,
      });
    });
  }, [activeStepIndex]);

  const buildDeviceFields = (data: DeviceData, verdict: string): Array<Field> => [
    { field_crm_id: "device_os", field_label: "device_os", field_value: data.os },
    // Lowercase os slug (ios | android) for the OS-specific help link in the
    // "incompatible" CMS page. Anything that isn't iOS maps to android.
    { field_crm_id: "device_os_slug", field_label: "device_os_slug", field_value: data.os.toLowerCase() === "ios" ? "ios" : "android" },
    { field_crm_id: "device_os_version", field_label: "device_os_version", field_value: data.osVersion },
    { field_crm_id: "device_family", field_label: "device_family", field_value: data.family },
    { field_crm_id: "device_is_mobile", field_label: "device_is_mobile", field_value: String(data.isMobile) },
    { field_crm_id: "device_compat", field_label: "device_compat", field_value: verdict },
    { field_crm_id: "device_info_source", field_label: "device_info_source", field_value: data.source },
  ];

  const submitOutcome = (data: DeviceData) => {
    const verdict = evaluateCompat(data.os, data.osVersion);
    // Route by verdict using only the standard step mechanics — no change to
    // the shared transition logic:
    //   supported   → jump to the configured `nextStep` (the normal continue
    //                 target), skipping the "incompatible" page entirely.
    //   unsupported → empty next_step, so getNextStepIndex falls through to the
    //                 sequentially-next step, which MUST be the "incompatible"
    //                 info page. That page is an isSuccessPage, so the form is
    //                 submitted as completed and the flow ends there.
    const routeNextStep = verdict === "supported" ? nextStep : "";
    const payload: StepData = {
      fields: buildDeviceFields(data, verdict),
      label,
      uuid,
      settings: {
        tracking: { gtmEvent: `formClient.step.${label}` },
        classes,
        custom_next_button: customNextButton,
        next_step: routeNextStep,
        last_step: lastStep,
        crm_field: "device_compat",
      },
    };
    handleStepSubmit(context, payload);
  };

  const handleManualSubmit = (os: string, osVersion: string, model: string) => {
    submitOutcome({
      source: "manual",
      os,
      osVersion,
      family: model || `${os} ${osVersion}`,
      isMobile: initialInfo?.is_mobile === "true",
    });
  };

  if (!screen) return null;

  return (
    <div className={b()}>
      {(screen === "SCREEN_1B_MANUAL" || screen === "SCREEN_1B_FALLBACK") && (
        <Screen1BManual
          stepData={stepData}
          showDetectionFailedBanner={detectionFailed && screen === "SCREEN_1B_FALLBACK"}
          onConfirm={handleManualSubmit}
        />
      )}
    </div>
  );
};

export default StepDeviceCheck;
